import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import JSONLD from '@/components/feature/JSONLD';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  const allArticles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.kaiyun-login.com/' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `https://www.kaiyun-login.com/category/${slug}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <JSONLD data={breadcrumbSchema} />
      <SEOHead
        title={`${category.name} - 开云登录 | 开云登录官网`}
        description={category.description}
        keywords={`开云登录,${category.name},开云登录入口,开云官方登录`}
        canonicalUrl={`https://www.kaiyun-login.com/category/${slug}`}
      />

      <Navbar />

      <main className="pt-20">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: category.name, path: `/category/${slug}` }]} />
          </div>
        </div>

        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {category.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-600">
                {category.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 border border-background-300/60 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {allArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allArticles.map((article) => {
                  const articleImages: Record<string, string> = {
                    'article-security-001': 'https://readdy.ai/api/search-image?query=Chinese%20technology%20blog%20article%20header%20image%20about%20login%20security%20protection%2C%20warm%20minimalist%20style%20with%20soft%20lighting%2C%20clean%20background%2C%20professional%20atmosphere%2C%20shield%20icon%20concept%2C%20warm%20coral%20and%20neutral%20tones%2C%20corporate%20blog%20aesthetic&width=800&height=450&seq=cat-article-security-001&orientation=landscape',
                    'article-device-002': 'https://readdy.ai/api/search-image?query=Chinese%20technology%20blog%20article%20header%20image%20about%20multi%20device%20login%2C%20warm%20minimalist%20style%20with%20soft%20lighting%2C%20clean%20background%2C%20smartphone%20and%20laptop%20concept%2C%20warm%20amber%20tones%2C%20corporate%20blog%20aesthetic&width=800&height=450&seq=cat-article-device-002&orientation=landscape',
                    'article-update-003': 'https://readdy.ai/api/search-image?query=Chinese%20technology%20blog%20article%20header%20image%20about%20software%20update%2C%20warm%20minimalist%20style%20with%20soft%20lighting%2C%20clean%20background%2C%20modern%20interface%20concept%2C%20warm%20coral%20tones%2C%20corporate%20blog%20aesthetic&width=800&height=450&seq=cat-article-update-003&orientation=landscape',
                    'article-enterprise-004': 'https://readdy.ai/api/search-image?query=Chinese%20technology%20blog%20article%20header%20image%20about%20enterprise%20solution%2C%20warm%20minimalist%20style%20with%20soft%20lighting%2C%20clean%20background%2C%20business%20concept%2C%20warm%20amber%20and%20neutral%20tones%2C%20corporate%20blog%20aesthetic&width=800&height=450&seq=cat-article-enterprise-004&orientation=landscape',
                    'article-faq-005': 'https://readdy.ai/api/search-image?query=Chinese%20technology%20blog%20article%20header%20image%20about%20account%20security%20FAQ%2C%20warm%20minimalist%20style%20with%20soft%20lighting%2C%20clean%20background%2C%20question%20and%20answer%20concept%2C%20warm%20coral%20tones%2C%20corporate%20blog%20aesthetic&width=800&height=450&seq=cat-article-faq-005&orientation=landscape',
                    'article-trend-006': 'https://readdy.ai/api/search-image?query=Chinese%20technology%20blog%20article%20header%20image%20about%20industry%20trend%202026%2C%20warm%20minimalist%20style%20with%20soft%20lighting%2C%20clean%20background%2C%20future%20technology%20concept%2C%20warm%20amber%20tones%2C%20corporate%20blog%20aesthetic&width=800&height=450&seq=cat-article-trend-006&orientation=landscape',
                  };

                  return (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200/60 transition-all duration-300"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={articleImages[article.imageSeq] || articleImages['article-security-001']}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                          <span className="whitespace-nowrap">{article.date}</span>
                          <span className="whitespace-nowrap">{article.readTime}阅读</span>
                        </div>
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200/70 mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}